import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, Tag, Edit, DollarSign, CheckCircle2, XCircle } from 'lucide-react';
import { listCreditPacks, activateCreditPack, deactivateCreditPack } from '../services/credit-pack.service';
import { CreditPackRepriceModal } from './CreditPackRepriceModal';
import { CreditPackForm } from './CreditPackForm';
import { TableSkeleton } from '@/src/modules/components/ui/LoadingSkeleton';
import { EmptyState, ErrorState } from '@/src/modules/components/ui/EmptyState';
import type { AdminCreditPackRow } from '../types/credit-pack.types';

export function CreditPackTable() {
  const queryClient = useQueryClient();
  const [editingPack, setEditingPack] = useState<AdminCreditPackRow | null>(null);
  const [repricingPack, setRepricingPack] = useState<AdminCreditPackRow | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  const { data: packs, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'credit-packs'],
    queryFn: () => listCreditPacks(),
    staleTime: 30_000,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      if (isActive) {
        await deactivateCreditPack(id);
      } else {
        await activateCreditPack(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'credit-packs'] });
    },
  });

  if (isLoading) return <TableSkeleton rows={5} />;
  if (isError) return <ErrorState message={error instanceof Error ? error.message : 'Failed to load credit packs'} onRetry={refetch} />;
  if (!packs || packs.length === 0) {
    return (
      <EmptyState
        title="No Partner Credit Packs Found"
        hint="Partner Credit Packs define the 10/30/73 volume credit ladder with permanent feature entitlements."
      />
    );
  }

  const sortedPacks = [...packs].sort((a, b) => a.displayOrder - b.displayOrder || a.code.localeCompare(b.code));

  return (
    <div className="space-y-4">
      <div className="bg-surface-container-low border border-border-subtle rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-border-subtle bg-surface-container-lowest/50 text-[10px] uppercase font-bold tracking-wider text-on-surface-variant">
                <th className="py-3 px-4">Pack</th>
                <th className="py-3 px-4">Unit</th>
                <th className="py-3 px-4">Credits</th>
                <th className="py-3 px-4">Sticker Price</th>
                <th className="py-3 px-4">Unit Pricing</th>
                <th className="py-3 px-4">Margin %</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/50">
              {sortedPacks.map((pack) => (
                <tr key={pack.id} className="hover:bg-surface-container-lowest/30 transition-colors">
                  <td className="py-3.5 px-4">
                    <div className="font-semibold text-on-surface">{pack.displayName}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-on-surface-variant font-mono">{pack.code}</span>
                      <span className="text-[10px] px-1.5 py-0.2 rounded bg-surface-container-high text-on-surface-variant">
                        v{pack.version}
                      </span>
                    </div>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-500/10 text-slate-300 border border-slate-500/20">
                      {pack.unit === 'VALUE' ? 'WALLET' : 'PUBLISH'}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 font-bold text-on-surface">
                    {pack.credits} <span className="text-xs font-normal text-on-surface-variant">credits</span>
                  </td>
                  <td className="py-3.5 px-4 font-semibold text-on-surface">
                    ₹{(pack.amountPaise / 100).toLocaleString('en-IN')}
                  </td>
                  <td className="py-3.5 px-4 text-xs text-on-surface-variant">
                    <div>Gross: <span className="font-medium text-on-surface">₹{pack.grossPricePerCredit.toFixed(2)}</span></div>
                    <div>Net: <span className="font-medium text-on-surface">₹{pack.netPricePerCredit.toFixed(2)}</span></div>
                  </td>
                  <td className="py-3.5 px-4">
                    {pack.unit === 'VALUE' ? (
                      // Wallet packs: the pack ratio is the discount (ADR-029); the per-publish margin math does not apply.
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-bold bg-primary/10 text-primary">
                        {pack.discountPercent}% off
                      </span>
                    ) : (
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                        pack.marginPercent >= 75
                          ? 'bg-emerald-500/15 text-emerald-400'
                          : 'bg-amber-500/15 text-amber-400'
                      }`}
                    >
                      {pack.marginPercent.toFixed(1)}%
                    </span>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        pack.isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'bg-zinc-500/10 text-zinc-400'
                      }`}
                    >
                      {pack.isActive ? (
                        <>
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3" /> Inactive
                        </>
                      )}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 text-right relative">
                    <button
                      onClick={() => setActiveMenuId(activeMenuId === pack.id ? null : pack.id)}
                      className="p-1.5 rounded-lg hover:bg-surface-container-high text-on-surface-variant hover:text-on-surface transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    {activeMenuId === pack.id && (
                      <div className="absolute right-4 mt-1 w-44 rounded-xl bg-surface-container-high border border-border-subtle shadow-xl z-20 py-1 text-xs">
                        <button
                          onClick={() => {
                            setRepricingPack(pack);
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-container-highest text-left text-on-surface transition-colors"
                        >
                          <DollarSign className="w-3.5 h-3.5 text-electric-violet" />
                          Reprice (New Version)
                        </button>
                        <button
                          onClick={() => {
                            setEditingPack(pack);
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-container-highest text-left text-on-surface transition-colors"
                        >
                          <Edit className="w-3.5 h-3.5 text-blue-400" />
                          Edit Metadata
                        </button>
                        <button
                          onClick={() => {
                            toggleActiveMutation.mutate({ id: pack.id, isActive: pack.isActive });
                            setActiveMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-container-highest text-left text-on-surface transition-colors"
                        >
                          {pack.isActive ? (
                            <>
                              <XCircle className="w-3.5 h-3.5 text-amber-400" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                              Activate
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreditPackRepriceModal
        open={!!repricingPack}
        pack={repricingPack}
        onClose={() => setRepricingPack(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'credit-packs'] });
        }}
      />

      <CreditPackForm
        open={!!editingPack}
        mode="edit"
        pack={editingPack}
        onClose={() => setEditingPack(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin', 'credit-packs'] });
        }}
      />
    </div>
  );
}
